import { onCocoEvent, COCO_EVENTS } from '@theme/theme-events';

const CE_NAME = 'coco-gift-recipient';

/**
 * Gift card “send to recipient” fields.
 * Custom element: <coco-gift-recipient>
 * Keeps Shopify property names required by the platform.
 */
class CocoGiftRecipient extends HTMLElement {
  #sectionId = '';
  #variantId = '';
  #checkbox = null;
  #fields = null;
  #liveRegion = null;
  #control = null;
  #offset = null;
  #email = null;
  #name = null;
  #message = null;
  #sendOn = null;
  #errorWrap = null;
  #errorList = null;
  #closeBtn = null;
  #unsubCart = null;
  #unsubVariant = null;
  #unsubError = null;
  #onChangeBound = null;
  #onCloseBound = null;
  #onErrorLinkBound = null;
  #onDocClickBound = null;
  #ready = false;

  connectedCallback() {
    if (this.#ready) return;
    this.#ready = true;

    this.#sectionId = this.dataset.sectionId || '';
    this.#variantId = this.dataset.productVariantId || '';

    this.#liveRegion = this.querySelector(`[data-coco-gift-live="${this.#sectionId}"]`);
    this.#checkbox = this.querySelector(`[data-coco-gift-checkbox="${this.#sectionId}"]`);
    this.#fields = this.querySelector('[data-recipient-fields]');
    this.#control = this.querySelector(`[data-coco-gift-control="${this.#sectionId}"]`);
    this.#email = this.querySelector(`[data-coco-gift-email="${this.#sectionId}"]`);
    this.#name = this.querySelector(`[data-coco-gift-name="${this.#sectionId}"]`);
    this.#message = this.querySelector(`[data-coco-gift-message="${this.#sectionId}"]`);
    this.#sendOn = this.querySelector(`[data-coco-gift-send-on="${this.#sectionId}"]`);
    this.#offset = this.querySelector(`[data-coco-gift-offset="${this.#sectionId}"]`);

    if (!this.#checkbox || !this.#fields) return;

    this.#checkbox.disabled = false;
    if (this.#control) this.#control.disabled = true;
    if (this.#offset) this.#offset.value = String(new Date().getTimezoneOffset());

    this.#errorWrap = this.querySelector('[data-error]');
    this.#errorList = this.#errorWrap?.querySelector('ul') || null;
    this.#closeBtn = this.#fields.querySelector('[is="close-drawer"]');

    this.#onChangeBound = () => this.#syncFromCheckbox();
    this.#onCloseBound = (event) => this.#onClose(event);
    this.#onErrorLinkBound = (event) => this.#onErrorLink(event);
    this.#onDocClickBound = (event) => this.#onDocumentClick(event);

    this.addEventListener('change', this.#onChangeBound);
    this.#closeBtn?.addEventListener('click', this.#onCloseBound);
    this.#errorWrap?.addEventListener('click', this.#onErrorLinkBound);

    if (this.dataset.hasErrors === 'true' || this.querySelector('[aria-invalid="true"]')) {
      this.#checkbox.checked = true;
    }

    this.#syncFromCheckbox();

    this.#unsubCart = onCocoEvent(COCO_EVENTS.cartUpdated, (event) => {
      if (event.source === 'coco-atc' && event.productVariantId?.toString() === this.#variantId) {
        this.#reset();
      }
    });

    this.#unsubVariant = onCocoEvent(COCO_EVENTS.variantChanged, (event) => {
      if (event.sectionId === this.dataset.sectionId) {
        this.#variantId = event.variant.id.toString();
      }
    });

    this.#unsubError = onCocoEvent(COCO_EVENTS.cartError, (event) => {
      if (event.source !== 'coco-atc' || event.productVariantId?.toString() !== this.#variantId) return;
      if (!this.#checkbox?.checked) return;
      this.#showErrors(event.message, event.errors || event.description);
    });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChangeBound);
    this.#closeBtn?.removeEventListener('click', this.#onCloseBound);
    this.#errorWrap?.removeEventListener('click', this.#onErrorLinkBound);
    document.removeEventListener('click', this.#onDocClickBound);
    this.#unsubCart?.();
    this.#unsubVariant?.();
    this.#unsubError?.();
  }

  get #paymentButton() {
    return this.closest('form')?.querySelector('[data-dynamic-checkout]');
  }

  get #isOpen() {
    return !this.#fields?.classList.contains('hidden');
  }

  #inputs() {
    return [this.#email, this.#name, this.#message, this.#sendOn].filter(Boolean);
  }

  #disableable() {
    return [...this.#inputs(), this.#offset].filter(Boolean);
  }

  #onClose(event) {
    event.preventDefault();
    this.#hideFields();
  }

  #onDocumentClick(event) {
    if (!this.#isOpen) return;
    if (event.target.closest('a[href^="#CocoGift-"]')) return;
    if (this.#fields.contains(event.target)) return;
    if (this.contains(event.target)) return;
    if (this.closest('form')?.contains(event.target)) return;
    this.#hideFields();
  }

  #onErrorLink(event) {
    const anchor = event.target.closest('a[href^="#CocoGift-"]');
    if (!anchor) return;

    event.preventDefault();
    event.stopPropagation();

    const input = document.getElementById(anchor.getAttribute('href').slice(1));
    if (!input) return;

    input.focus();
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  #hideFields() {
    if (!this.#isOpen) return;
    document.removeEventListener('click', this.#onDocClickBound);
    this.#fields.classList.add('hidden');
  }

  #syncFromCheckbox() {
    if (this.#checkbox.checked) {
      this.#setFieldsEnabled(true);
      this.#fields.classList.remove('hidden');
      this.#paymentButton?.classList.add('hidden');
      if (this.#liveRegion) {
        this.#liveRegion.innerText = window.accessibilityStrings?.recipientFormExpanded || '';
      }
      setTimeout(() => {
        document.addEventListener('click', this.#onDocClickBound);
      }, 0);
      return;
    }

    document.removeEventListener('click', this.#onDocClickBound);
    this.#clearInputs();
    this.#setFieldsEnabled(false);
    this.#clearErrors();
    this.#fields.classList.add('hidden');
    this.#paymentButton?.classList.remove('hidden');
    if (this.#liveRegion) {
      this.#liveRegion.innerText = window.accessibilityStrings?.recipientFormCollapsed || '';
    }
  }

  #clearInputs() {
    this.#inputs().forEach((field) => {
      field.value = '';
    });
  }

  #setFieldsEnabled(enabled) {
    this.#disableable().forEach((field) => {
      field.disabled = !enabled;
    });
    if (this.#control) this.#control.disabled = enabled;
  }

  #fieldSelector(key) {
    if (key === 'send_on') return `#CocoGift-send-on-${this.#sectionId}`;
    return `#CocoGift-${key}-${this.#sectionId}`;
  }

  #inputForKey(key) {
    return {
      email: this.#email,
      name: this.#name,
      message: this.#message,
      send_on: this.#sendOn,
    }[key];
  }

  #showErrors(title, body) {
    if (!this.#errorWrap || !this.#checkbox?.checked) return;

    this.#clearErrors();
    this.#errorWrap.hidden = false;
    this.#fields.classList.remove('hidden');

    if (body && typeof body === 'object') {
      Object.entries(body).forEach(([key, value]) => {
        const message = `${Array.isArray(value) ? value.join(', ') : value}`;

        if (this.#errorList) {
          this.#errorList.appendChild(this.#errorListItem(this.#fieldSelector(key), message));
        }

        this.#inputForKey(key)?.setAttribute('aria-invalid', 'true');
      });
      return;
    }

    if (this.#errorList && (body || title)) {
      const li = document.createElement('li');
      li.textContent = body || title;
      this.#errorList.appendChild(li);
    }
  }

  #errorListItem(href, message) {
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.setAttribute('href', href);
    a.innerText = message;
    li.appendChild(a);
    return li;
  }

  #clearErrors() {
    if (!this.#errorWrap) return;

    this.#errorWrap.hidden = true;
    if (this.#errorList) this.#errorList.innerHTML = '';

    this.#inputs().forEach((input) => {
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    });
  }

  #reset() {
    if (!this.#checkbox.checked) return;
    this.#checkbox.checked = false;
    this.#clearInputs();
    this.#clearErrors();
    this.#syncFromCheckbox();
  }
}

if (!customElements.get(CE_NAME)) {
  customElements.define(CE_NAME, CocoGiftRecipient);
}
